import { Core } from "@faire/design-tokens";
import { trackSignupModalOpenFdHomepageBottomCtaClick } from "@faire/web-api/events/signup/click/modalOpenFdHomepageBottomCta";
import { IBrand } from "@faire/web-api/indigofair/data/IBrand";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { optimizeImageUrlHeight } from "@faire/web/common/images/optimizeImages";
import { faireBaseUrl } from "@faire/web/common/routes/faireBaseUrl";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { useIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";

import { SignUpButton } from "../components/SignUpButton";

const FooterSignUpBase: React.FC<{
  brand: IBrand;
  loginUrl?: string;
}> = ({ brand, loginUrl }) => {
  const isSSR = useIsSSR();

  const imageUrl = optimizeImageUrlHeight(
    IImage.build(brand.banner_image),
    isSSR,
    440
  );

  if (!brand.name) {
    return null;
  }

  return (
    <Wrapper $imageUrl={imageUrl}>
      <OpacityLayer>
        <Column align="center" justify="center">
          <Header>
            <StrictLocalMsg
              defaultMessage="Ready to start ordering {brandCompanyName} online?"
              description={{
                text: "Footer banner that prompts users to sign up for an account in order to order with their Faire Direct brand.",
                img: "https://cdn.faire.com/fastly/9c7872d2e9356d6ffafdc1d32920ec119ccaba4e3aabc219d19179a2e21928fc.png",
              }}
              values={{
                brandCompanyName: brand.name,
              }}
            />
          </Header>
          <SignUpButton />
          <Spacer size="2x" />
          <SignInLink loginUrl={loginUrl} />
        </Column>
      </OpacityLayer>
    </Wrapper>
  );
};

export const FooterSignUp = observer(FooterSignUpBase);

interface SignInLinkProps {
  loginUrl?: string;
}

const SignInLink: React.FC<SignInLinkProps> = ({ loginUrl }) => {
  const isBrandPreview = useIsBrandPreview();

  if (isBrandPreview) {
    return null;
  }

  return (
    <Button
      variant="tertiary"
      linkTo={loginUrl ?? faireBaseUrl()}
      onClick={trackSignupModalOpenFdHomepageBottomCtaClick}
      tertiaryColor={Core.text.inverse}
    >
      <StrictLocalMsg defaultMessage="Sign into Faire Account" />
    </Button>
  );
};

const Wrapper = styled(Row).attrs({ align: "center", justify: "center" })<{
  $imageUrl?: string;
}>`
  width: 100%;
  height: 440px;
  background-size: cover;
  background-position: center;
  position: relative;

  background-image: ${(props) => `url(${props.$imageUrl})`};

  ${responsiveMediaQueries.mobile} {
    height: 383px;
  }
`;

const OpacityLayer = styled(Row).attrs({ align: "center", justify: "center" })`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Header = styled(Typography).attrs({
  variant: "displayLSerifRegular",
  align: "center",
  color: "white",
})`
  &&& {
    font-family: "Big Caslon", serif;
    max-width: 591px;
    margin-bottom: 32px;

    ${responsiveMediaQueries.mobile} {
      max-width: 279px;
      font-size: 34px;
      line-height: 40px;
    }
  }
`;
